/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { useReducer, useEffect } from 'react';
import { connect } from 'mqtt';
import noop from 'lodash/noop';

/**
 *
 * @param {String} id
 */
const getMonitoringTopic = id => `iot-2/type/${id.split(':')[0]}/id/${id.split(':')[1]}/mon`;

/**
 *
 * @param {Object} credentials
 * @param {Array<String>} typeAndDeviceIds  array of "typeId:deviceId" strings
 * @param {Function} logger  callback with debug messages
 * @param {boolean} isICP mas environment use different mqtt endpoint
 */
const useConnectionMonitor = (
  credentials = {},
  typeAndDeviceIds = [],
  logger = noop,
  isICP = false
) => {
  const [state, dispatch] = useReducer(
    (reducerState, { type, payload }) => {
      // console.log('useConnectionMonitor reducer: ', reducerState, type, payload);
      switch (type) {
        case 'setClient':
          return {
            ...reducerState,
            client: payload,
          };
        case 'setIsConnecting':
          return {
            ...reducerState,
            isConnecting: payload,
          };
        case 'setIsConnected':
          return {
            ...reducerState,
            isConnected: payload,
          };
        case 'update':
          return {
            ...reducerState,
            connectionDetails: {
              ...reducerState.connectionDetails,
              [payload.id]: payload.details,
            },
          };
        case 'watch':
          return {
            ...reducerState,
            watchList: reducerState.watchList.concat([payload.id]),
            connectionDetails: {
              ...reducerState.connectionDetails,
              [payload.id]: {
                startTime: new Date().getTime(),
              },
            },
          };
        case 'unwatch':
          return {
            ...reducerState,
            watchList: reducerState.watchList.filter(i => i !== payload.id),
          };
        default:
          throw new Error();
      }
    },
    {
      client: null,
      isConnecting: false,
      isConnected: false,
      connectionDetails: {},
      watchList: [],
    }
  );

  // TODO: batch subscribes until after the client has connected
  const add = (mqttClient, ids) =>
    ids.forEach(id => {
      mqttClient.subscribe(getMonitoringTopic(id), err =>
        err ? logger(err) : logger(`subscribed to ${getMonitoringTopic(id)}`)
      );
      dispatch({ type: 'watch', payload: { id } });
    });

  const remove = (mqttClient, ids) =>
    ids.forEach(id => {
      mqttClient.unsubscribe(getMonitoringTopic(id), err =>
        err ? logger(err) : logger(`unsubscribed from ${getMonitoringTopic(id)}`)
      );
      dispatch({ type: 'unwatch', payload: { id } });
    });

  const replaceIds = ids => {
    remove(state.client, state.watchList);
    add(state.client, ids);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setUpClient = () => {
    logger('connecting...');
    dispatch({ type: 'setIsConnecting', payload: true });

    // It should be like `wss://${tenantid}.messaging.iot.${hostaddress}:443`
    // example: wss://masdev.messaging.iot.monitordev3.monitordevcluster-d3bcf8b5e47345c6ea924110d640b819-0000.us-south.containers.appdomain.cloud:443

    const wssUrl = isICP
      ? `${window.location.hostname.replace('.monitor.', '.messaging.iot.')}`
      : `${credentials.org}.messaging.internetofthings.ibmcloud.com`;

    const clientId = `a:${credentials.org}:${Date.now()}`;
    // eslint-disable-next-line react-redux/connect-prefer-named-arguments
    const c = connect(`wss://${wssUrl}:443`, {
      clientId,
      username: credentials.apiKey,
      password: credentials.apiToken,
      rejectUnauthorized: false,
    });

    c.on('connect', () => {
      dispatch({ type: 'setIsConnected', payload: true });
      dispatch({ type: 'setIsConnecting', payload: false });
      logger('connected!');
      add(c, typeAndDeviceIds);
    });
    c.on('close', () => {
      dispatch({ type: 'setIsConnected', payload: false });
      logger('disconnecting');
    });
    c.on('end', () => {
      dispatch({ type: 'setIsConnected', payload: false });
      logger('disconnected');
    });
    c.on('message', (topic, payload) => {
      const payloadString = new TextDecoder().decode(payload);
      if (payloadString !== '') {
        const typeId = topic.split('/')[2];
        const deviceId = topic.split('/')[4];

        try {
          const msgObj = JSON.parse(payloadString);
          logger(`received msg - topic: ${topic}`, msgObj);
          const { Action, Time, ClientAddr, Secure } = msgObj;

          if (Action === 'Connect') {
            dispatch({
              type: 'update',
              payload: {
                id: `${typeId}:${deviceId}`,
                details: {
                  typeId,
                  deviceId,
                  action: 'Connect',
                  connectTime: Time,
                  clientAddr: ClientAddr,
                  isSecure: Secure,
                },
              },
            });
          } else if (Action === 'Disconnect') {
            const { ConnectTime, ReadBytes, WriteBytes, CloseCode } = msgObj;
            // don't treat CloseCode = 288 as a true disconnect
            if (CloseCode !== 288) {
              dispatch({
                type: 'update',
                payload: {
                  id: `${typeId}:${deviceId}`,
                  details: {
                    typeId,
                    deviceId,
                    action: 'Disconnect',
                    connectTime: ConnectTime,
                    disconnectTime: Time,
                    clientAddr: ClientAddr,
                    closeCode: CloseCode,
                    isSecure: Secure,
                    readBytes: ReadBytes,
                    writeBytes: WriteBytes,
                  },
                },
              });
            }
          }
        } catch (e) {
          logger(`formatting error`);
        }
      }
    });
    dispatch({ type: 'setClient', payload: c });
  };

  useEffect(() => {
    if (!state.isConnected && !state.isConnecting && credentials.org) {
      // console.log(state.isConnected, state.isConnecting, credentials.org);
      setUpClient();
    }
  }, [state.isConnected, state.isConnecting, credentials.org, setUpClient]);

  return [state.connectionDetails, state.isConnected, replaceIds];
};

export default useConnectionMonitor;
